/**
 * Native navigation chrome for the phone.
 *
 * The shell ships a fixed header that is a *branding* bar: wordmark, release
 * badge, token count, profile and help. It costs ~56px on every screen and
 * never tells you which screen you are on. Native apps spend that strip on the
 * screen's own title, and use the platform's large-title pattern: the title
 * starts large in the content, and collapses into the bar as you scroll.
 *
 * This module implements that pattern against the markup the core already
 * renders. It reads whichever title the current view produced, mirrors it into
 * the bar, and swaps the wordmark for it once the large one has scrolled away.
 *
 * Nothing here runs on desktop — the sidebar carries wayfinding there.
 */

import { onRendered, onRoute } from '../runtime/legacy.js';

/** Where each view puts its own heading, most specific first. */
const TITLE_SOURCES = ['.page-title', '.greeting', '.card-title'];

let observer = null;

function headerEl() {
  return document.getElementById('mobile-header');
}

function ensureTitleSlot() {
  const header = headerEl();
  if (!header) return null;
  let slot = header.querySelector('.mobile-header-title');
  if (slot) return slot;

  slot = document.createElement('span');
  slot.className = 'mobile-header-title';
  // Announced by the large heading in the content; this is its shrunken twin.
  slot.setAttribute('aria-hidden', 'true');
  const left = header.querySelector('.mobile-header-left');
  (left || header).appendChild(slot);
  return slot;
}

function findTitle(root) {
  for (const selector of TITLE_SOURCES) {
    const el = root.querySelector(selector);
    if (el && el.textContent.trim()) return el;
  }
  return null;
}

/**
 * Collapses the bar once the view's own heading has scrolled out of sight.
 * An IntersectionObserver rather than a scroll handler: it fires only at the
 * threshold, so nothing runs on every frame of a flick.
 */
function watchTitle(titleEl) {
  observer?.disconnect();
  const header = headerEl();
  if (!header || !titleEl) return;

  observer = new IntersectionObserver(
    ([entry]) => header.classList.toggle('is-collapsed', !entry.isIntersecting),
    // The heading counts as "gone" once it is under the bar, not off-screen.
    { rootMargin: '-56px 0px 0px 0px', threshold: 0 },
  );
  observer.observe(titleEl);
}

function sync(root) {
  const slot = ensureTitleSlot();
  const titleEl = findTitle(root);
  const header = headerEl();
  if (!slot || !header) return;

  if (!titleEl) {
    // A view with no heading of its own keeps the wordmark and never collapses.
    slot.textContent = '';
    header.classList.remove('is-collapsed', 'has-title');
    observer?.disconnect();
    return;
  }

  slot.textContent = titleEl.textContent.trim();
  header.classList.add('has-title');
  header.classList.remove('is-collapsed');
  watchTitle(titleEl);
}

export function installMobileChrome() {
  const container = () => document.getElementById('view-container');

  // Views replace the container's contents, so re-read on every render.
  onRendered('#view-container', (el) => sync(el));
  const observeRenders = () => {
    const el = container();
    if (!el) return;
    new MutationObserver(() => sync(el)).observe(el, { childList: true });
    sync(el);
  };

  // A route change scrolls to the top; the bar must come back with it.
  onRoute(() => {
    const el = container();
    headerEl()?.classList.remove('is-collapsed');
    if (el) sync(el);
  });

  if (document.body) observeRenders();
  else document.addEventListener('DOMContentLoaded', observeRenders, { once: true });
}
